import React from 'react';
import { logger } from '../helpers/logger';
import { BASE_URL } from '../helpers/urlHelper';
import { executeRecaptcha } from '../views/links/Links';
import { NavigateFunction } from 'react-router-dom';

export interface PotentialAnswers {
  description: string;
  id: string;
  order: number;
  text: string;
}

export interface IChallengeItem {
  children: IChallengeItem[] | null;
  attributes: { [key: string]: string } | null;
  component: string;
  dataId: string | null;
  defaultValue: string | number | null;
  description: string | null;
  inputType: string | null;
  mask: string | null;
  placeholder: string | null;
  text: string | null;
  tooltip: string | null;
  order: number;
  potentialAnswers: PotentialAnswers[];
  validations: { [key: string]: string }[] | number;
  classes: string[];
  uniqueId: string;
}

export interface IChallengeItem {
  children: IChallengeItem[] | null;
  attributes: { [key: string]: string } | null;
  component: string;
  dataId: string | null;
  defaultValue: string | number | null;
  description: string | null;
  inputType: string | null;
  mask: string | null;
  placeholder: string | null;
  text: string | null;
  tooltip: string | null;
  order: number;
  potentialAnswers: PotentialAnswers[];
  validations: { [key: string]: string }[] | number;
  classes: string[];
  uniqueId: string;
}

export enum linksReducerActions {
  SET_LINKS_LOADING = 'SET_LINKS_LOADING',
  FETCH_LINKS_NEXT = 'FETCH_LINKS_NEXT',
  SET_LINKS_ANSWERS_LOADING = 'SET_LINKS_ANSWERS_LOADING',
  SEND_LINKS_ANSWERS = 'SEND_LINKS_ANSWERS',
}

export interface IAction {
  type: string;
  payload?: any;
  isLoading?: boolean;
}

interface IState {
  links: any;
  isLinksLoading: boolean;
}
type LinksProviderProps = { children: React.ReactNode };

const LinksStateContext = React.createContext<{ state: IState; dispatch: React.Dispatch<IAction> } | undefined>({
  state: {
    links: {},
    isLinksLoading: false,
  },
  dispatch: () => null,
});

const linksReducer = (state: IState, action: IAction): IState => {
  switch (action.type) {
    case linksReducerActions.SET_LINKS_LOADING: {
      return { ...state, isLinksLoading: !!action.isLoading };
    }
    case linksReducerActions.FETCH_LINKS_NEXT: {
      return { ...state, links: action.payload, isLinksLoading: false };
    }
    default: {
      return state;
    }
  }
};

const LinksProvider = ({ children }: LinksProviderProps): JSX.Element => {
  const [state, dispatch] = React.useReducer(linksReducer, {
    links: {},
    isLinksLoading: false,
  });
  const value = { state, dispatch };

  return <LinksStateContext.Provider value={value}>{children}</LinksStateContext.Provider>;
};

const useLinks = (): { state: IState; dispatch: React.Dispatch<IAction> } => {
  const context = React.useContext(LinksStateContext);
  if (context === undefined) {
    throw new Error('useLinks must be used within a LinksProvider');
  }
  return context;
};

export const fetchNext = (dispatch: React.Dispatch<IAction>, id: string, navigate: NavigateFunction): void => {
  dispatch({ type: linksReducerActions.SET_LINKS_LOADING, isLoading: true });

  const url = BASE_URL.LINK_NEXT;
  const options = {
    method: 'POST',
    body: JSON.stringify({ id }),
    headers: { 'Content-Type': 'application/json' },
  };
  fetch(url, options)
    .then(response => {
      if (response && response.status >= 200 && response.status < 300) {
        return response.json();
      } else {
        throw response.status;
      }
    })
    .then(response => {
      dispatch({ type: linksReducerActions.FETCH_LINKS_NEXT, payload: response ?? {} });
    })
    .catch(error => {
      logger({ name: 'error', message: error.message });
      dispatch({ type: linksReducerActions.FETCH_LINKS_NEXT, payload: {}, isLoading: false });
      navigate('/error');
      // // eslint-disable-next-line no-restricted-globals
      // parent.postMessage({ message: 'checkoutDecline' }, '*');
    });
};

interface ILinksAnswers {
  dispatch: React.Dispatch<IAction>;
  answers: { [key: string]: string | number | boolean | null | { [key: string]: string | number | boolean | null } };
  navigate: NavigateFunction;
}

export const sendLinksAnswers = async ({ dispatch, answers, navigate }: ILinksAnswers): Promise<void> => {
  dispatch({ type: linksReducerActions.SET_LINKS_LOADING, isLoading: true });

  const recaptchaToken = await executeRecaptcha();
  const body = {
    ...answers,
    recaptchaToken,
  };

  const url = BASE_URL.LINK_NEXT;
  const options = {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  };
  fetch(url, options)
    .then(response => {
      if (response && response.status >= 200 && response.status < 300) {
        return response.json();
      } else {
        throw response.status;
      }
    })
    .then(response => {
      if (response.result === 'SUCCESS') {
        // fetchNext(dispatch, response.id);
        fetchNext(dispatch, answers.id as string, navigate);
      } else {
        dispatch({ type: linksReducerActions.SET_LINKS_LOADING, isLoading: false });
      }
    })
    .catch(error => {
      logger({ name: 'error', message: error.message });
      dispatch({ type: linksReducerActions.SET_LINKS_LOADING, isLoading: false });
      navigate('/error');
    });
};

export { LinksProvider, useLinks };
